import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Eye, Plus } from "lucide-react";
import DocxUploader from "@/components/DocxUploader";
import { useAuth } from "@/lib/auth";

const STEPS = [
  { key: "org_name", q: "Quel est le nom de votre organisation ?", type: "text", placeholder: "Ex : Banque Genève" },
  { key: "secteur", q: "Dans quel secteur évoluez-vous ?", type: "single", options: ["Banque", "Assurance", "Fonds de pension", "Santé", "Organisation publique", "Industrie", "PME", "Autre"] },
  { key: "taille", q: "Quelle est la taille de votre organisation ?", type: "single", options: ["< 50", "50-250", "250-1000", "> 1000"] },
  // V23.14.9 — Profil libre (alimente tous les prompts LLM via inject_doctrine)
  {
    key: "profil",
    q: "Décrivez votre organisation en quelques phrases — mission, ADN, ce qui vous différencie réellement.",
    type: "textarea",
    placeholder: "Ex : Banque privée familiale (1875), nous accompagnons les entrepreneurs sur la durée. Notre force : un comité d'investissement qui valide chaque dossier en moins de 72h. Notre limite assumée : nous n'allons pas chercher les actifs liquides court terme — c'est notre choix.",
    optional: true,
  },
  // V23.14.9 — Niveau UI (adapte immédiatement l'interface)
  {
    key: "ui_level",
    q: "Quel niveau d'interface vous correspond ?",
    type: "single",
    options: ["Débutant", "Intermédiaire", "Expert"],
    hint: "Débutant : guides explicatifs partout. Intermédiaire : interface standard. Expert : données brutes + paramètres avancés.",
  },
  { key: "modules", q: "Quels modules souhaitez-vous activer ?", type: "multi", options: ["Annuaire", "Négociation", "Chaînage", "Pipeline / Origination", "Courtage", "Immobilier", "Veille stratégique", "Risques", "Conformité", "Audit", "Performance", "Continuité", "Fournisseurs", "Sécurité SI", "Gouvernance IA", "Doctrine"] },
  { key: "referentiels", q: "Quels référentiels vous concernent ?", type: "multi", options: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 27001", "DORA", "NIS2", "RGPD-LPD", "NIST", "PCI-DSS", "FINMA", "Aucun"] },
  { key: "maturite", q: "Quel est votre niveau de maturité actuel ?", type: "single", options: ["Débutant — zéro", "Intermédiaire — outils épars", "Avancé — consolider"] },
];

const UI_LEVEL_MAP = {
  "Débutant": "debutant",
  "Intermédiaire": "intermediaire",
  "Expert": "expert",
};

const INITIAL = {
  org_name: "", secteur: "", taille: "", profil: "", ui_level: "", modules: [], referentiels: [], maturite: "",
};

export default function Assistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAuthenticated = user && user !== false && user !== null;
  const isOnboarding = !!location.state?.onboarding;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(INITIAL);
  const [thinking, setThinking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [seedingPack, setSeedingPack] = useState(false);
  const [seedDone, setSeedDone] = useState(false);

  // Pre-fill from active workspace when authenticated, so renaming/changing sector here
  // updates the existing cockpit instead of starting blank.
  useEffect(() => {
    if (!isAuthenticated) return;
    api.get("/workspace/organisation").then((r) => {
      setAnswers((a) => ({
        ...a,
        org_name: a.org_name || r.data?.name || "",
        secteur: a.secteur || r.data?.sector || "",
        profil: a.profil || r.data?.profil || "",
      }));
    }).catch(() => {});
    // V23.14.9 — pré-remplir ui_level depuis le manifest persona courant
    api.get("/workspace/persona/me").then((r) => {
      const lvl = r.data?.ui_level;
      const labelMap = { debutant: "Débutant", intermediaire: "Intermédiaire", expert: "Expert" };
      if (lvl && labelMap[lvl]) {
        setAnswers((a) => ({ ...a, ui_level: a.ui_level || labelMap[lvl] }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const installStarterPack = async () => {
    setSeedingPack(true);
    try {
      // 1) When authenticated, persist the answers to the active organisation
      if (isAuthenticated) {
        try {
          await api.patch("/workspace/organisation", {
            name: answers.org_name || undefined,
            sector: answers.secteur || undefined,
            profil: answers.profil || undefined,
          });
          // Notify topbar to re-fetch
          window.dispatchEvent(new CustomEvent("noam:org-updated"));
        } catch {
          /* non-blocking — starter pack still proceeds */
        }
        // V23.14.9 — persister le niveau UI (Débutant/Intermédiaire/Expert)
        if (answers.ui_level && UI_LEVEL_MAP[answers.ui_level]) {
          try {
            await api.put("/workspace/persona/user", { ui_level: UI_LEVEL_MAP[answers.ui_level] });
            window.dispatchEvent(new Event("persona:changed"));
          } catch {
            /* non-blocking */
          }
        }
      }
      // 2) Install the sector-specific starter pack of risks
      const r = await api.post("/workspace/risks/starter-pack", { sector: answers.secteur });
      setSeedDone(r.data?.created || true);
      try { window.sessionStorage.setItem("noam_onboarded", "1"); }
      catch { /* silent — defensive */ }
      setTimeout(() => navigate("/app/risques"), 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible d'installer le référentiel.");
    } finally {
      setSeedingPack(false);
    }
  };

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const value = answers[current?.key];

  const canNext = current && (
    current.optional
      ? true
      : current.type === "multi"
        ? value.length > 0
        : (value && value.length > 0)
  );

  const setVal = (v) => setAnswers((a) => ({ ...a, [current.key]: v }));
  const toggle = (opt) => {
    const arr = answers[current.key];
    setVal(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
  };

  const next = async () => {
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    // last → submit
    setThinking(true);
    setError(null);
    try {
      // V23.14.9 — Persister profil libre + ui_level avant la génération du cockpit
      if (isAuthenticated) {
        try {
          await api.patch("/workspace/organisation", {
            name: answers.org_name || undefined,
            sector: answers.secteur || undefined,
            profil: answers.profil || undefined,
          });
          window.dispatchEvent(new CustomEvent("noam:org-updated"));
        } catch { /* non-blocking */ }
        if (answers.ui_level && UI_LEVEL_MAP[answers.ui_level]) {
          try {
            await api.put("/workspace/persona/user", { ui_level: UI_LEVEL_MAP[answers.ui_level] });
            window.dispatchEvent(new Event("persona:changed"));
          } catch { /* non-blocking */ }
        }
      }
      // P0 Cloudflare fix — pattern async : 202 + job_id + polling (aucune
      // requête HTTP longue → aucun timeout proxy possible)
      const start = await api.post("/assistant/generate", answers);
      const jobId = start.data.job_id;
      let done = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/assistant/generate/jobs/${jobId}`).catch(() => null);
        if (st?.data?.status === "completed") { done = st.data.result; break; }
        if (st?.data?.status === "failed") throw new Error(st.data.error || "Génération échouée — réessayez.");
      }
      if (!done) throw new Error("La génération prend trop de temps — réessayez.");
      setResult(done);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Erreur lors de la génération.");
    } finally {
      setThinking(false);
    }
  };

  const reset = () => {
    setStep(0); setAnswers(INITIAL); setResult(null); setError(null);
  };

  return (
    <div data-testid="assistant-root">
      {isOnboarding && isAuthenticated && (
        <div
          className="mb-4 p-4 rounded-lg border border-[#378ADD]/30 bg-gradient-to-r from-[#378ADD]/10 via-[#378ADD]/5 to-transparent flex items-start gap-3"
          data-testid="onboarding-banner"
        >
          <div className="w-9 h-9 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-[#378ADD]" />
          </div>
          <div className="flex-1">
            <div className="text-sm text-white font-medium">Bienvenue sur Noam {user?.name ? `, ${user.name}` : ""}.</div>
            <div className="text-xs text-zinc-300 mt-0.5">
              Votre espace est vide. En 8 questions, l&apos;Assistant configure votre univers de pilotage.
              Vous pouvez aussi <button onClick={() => navigate("/app/risques")} className="text-[#378ADD] hover:underline" data-testid="onboarding-skip">commencer manuellement par les risques</button>.
            </div>
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6 min-h-[calc(100vh-9rem)]">
      {/* LEFT: chat */}
      <div className="noam-card p-6 lg:p-8 flex flex-col">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-4 h-4 text-[#378ADD]" />
          <span className="noam-overline">NOAM</span>
          <span className="ml-auto text-xs text-zinc-500">{Math.min(step + 1, STEPS.length)} / {STEPS.length}</span>
        </div>

        {/* Progress */}
        <div className="h-1 rounded-full bg-white/5 mb-6 overflow-hidden">
          <div
            className="h-full bg-[#378ADD] transition-all duration-500"
            style={{ width: `${((result ? STEPS.length : step) / STEPS.length) * 100}%` }}
          />
        </div>

        {!result && (
          <div className="flex-1 flex flex-col">
            <div className="text-zinc-400 text-sm mb-2">Question {step + 1}</div>
            <h2 className="noam-display text-2xl font-bold tracking-tight mb-6">{current.q}</h2>

            {current.type === "text" && (
              <input
                autoFocus
                value={value}
                onChange={(e) => setVal(e.target.value)}
                className="noam-input text-lg"
                placeholder={current.placeholder}
                data-testid={`step-input-${current.key}`}
                onKeyDown={(e) => { if (e.key === "Enter" && canNext) next(); }}
              />
            )}

            {current.type === "textarea" && (
              <div className="space-y-2">
                <textarea
                  autoFocus
                  value={value}
                  onChange={(e) => setVal(e.target.value)}
                  className="noam-input text-base leading-relaxed min-h-[180px] resize-y"
                  placeholder={current.placeholder}
                  rows={6}
                  maxLength={2000}
                  data-testid={`step-textarea-${current.key}`}
                />
                <div className="flex items-center justify-between text-[10px] text-zinc-500">
                  <span className="italic">Cette description sera utilisée par NOAM pour personnaliser toutes vos analyses.</span>
                  <span>{(value || "").length} / 2000</span>
                </div>
              </div>
            )}

            {current.type === "single" && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {current.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setVal(opt)}
                      className={`noam-pill ${value === opt ? "selected" : ""}`}
                      data-testid={`step-option-${opt}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {current.hint && (
                  <p className="text-[11px] text-zinc-500 italic">{current.hint}</p>
                )}
              </div>
            )}

            {current.type === "multi" && (
              <div className="flex flex-wrap gap-2">
                {current.options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => toggle(opt)}
                    className={`noam-pill ${value.includes(opt) ? "selected" : ""}`}
                    data-testid={`step-multi-${opt}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}

            <div className="mt-auto pt-8 flex items-center justify-between">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="btn-ghost disabled:opacity-30"
                data-testid="assistant-back"
              >
                <ArrowLeft className="w-4 h-4" /> Précédent
              </button>
              <button
                onClick={next}
                disabled={!canNext || thinking}
                className="btn-primary disabled:opacity-50"
                data-testid="assistant-next"
              >
                {thinking ? "Génération…" : isLast ? "Générer mon espace" : "Suivant"}
                {!thinking && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>

            {error && <div className="mt-4 text-sm text-[#E24B4A]" data-testid="assistant-error">{error}</div>}
          </div>
        )}

        {result && (
          <div className="flex-1 flex flex-col" data-testid="assistant-result">
            <div className="flex items-center gap-2 text-[#1D9E75] mb-2">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-medium">Espace de travail généré</span>
            </div>
            <h2 className="noam-display text-2xl font-bold tracking-tight">{result.cockpit_name}</h2>
            <p className="text-zinc-300 mt-3">{result.summary}</p>

            <div className="mt-auto pt-8 flex flex-wrap gap-3">
              <button onClick={() => navigate("/app/dashboard")} className="btn-primary" data-testid="assistant-go-dashboard">
                Créer mon espace <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={reset} className="btn-ghost" data-testid="assistant-reset">
                Recommencer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: preview */}
      <div className="noam-card p-6 lg:p-8 bg-white/[0.015]">
        <div className="noam-overline mb-3">Prévisualisation de votre espace</div>
        <h3 className="noam-display text-lg font-semibold mb-5 transition-all">
          {answers.org_name ? `Espace ${answers.org_name}` : "Votre espace NOAM"}
        </h3>

        {!result && (
          <div className="space-y-4 text-sm">
            {/* Q1 — org */}
            {answers.org_name && (
              <div className="fade-up p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Organisation</div>
                <div className="text-white font-medium">{answers.org_name}</div>
              </div>
            )}
            {/* Q2 — sector + suggestions */}
            {answers.secteur && (
              <div className="fade-up p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Secteur · modules suggérés</div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{answers.secteur}</span>
                  {answers.taille && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#378ADD]/30 bg-[#378ADD]/10 text-[#B5D4F4]">{answers.taille}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {sectorSuggestions(answers.secteur).map((s, i) => (
                    <span key={s} className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400" style={{ animationDelay: `${i * 60}ms` }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
            {/* Q4 — selected modules with animation */}
            {answers.modules.length > 0 && (
              <div className="fade-up p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Modules activés</div>
                <div className="flex flex-wrap gap-1.5">
                  {answers.modules.map((m, i) => (
                    <span
                      key={m}
                      className="text-xs px-2 py-1 rounded-full text-white font-medium fade-up"
                      style={{
                        background: pillColor(i),
                        animationDelay: `${i * 80}ms`,
                      }}
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Q5 — référentiels */}
            {answers.referentiels.length > 0 && (
              <div className="fade-up p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Référentiels pré-chargés</div>
                <div className="flex flex-wrap gap-1.5">
                  {answers.referentiels.map((r, i) => (
                    <span key={r} className="text-[11px] px-2 py-0.5 rounded-full border border-[#378ADD]/30 bg-[#378ADD]/10 text-[#B5D4F4] fade-up" style={{ animationDelay: `${i * 60}ms` }}>{r}</span>
                  ))}
                </div>
              </div>
            )}
            {/* Q6 — maturity + recommended starting phase */}
            {answers.maturite && (
              <div className="fade-up p-3 rounded-lg border border-[#1D9E75]/30 bg-[#1D9E75]/5">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Maturité & phase de démarrage</div>
                <div className="text-white text-sm">{answers.maturite}</div>
                <div className="text-[11px] text-[#1D9E75] mt-1.5">
                  Phase recommandée : <span className="font-medium">{recommendPhase(answers.maturite)}</span>
                </div>
              </div>
            )}
            {/* Empty state */}
            {!answers.org_name && (
              <div className="text-zinc-500 text-xs italic">
                À mesure que vous répondez, votre espace se construit ici en temps réel…
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-6 text-sm fade-up">
            <div>
              <div className="noam-overline mb-2">Types personnalisés</div>
              <div className="flex flex-wrap gap-1.5">
                {result.custom_types.map((t, i) => (
                  <span key={`ct-${t.name}-${i}`} className="text-xs px-2.5 py-1 rounded-full border border-[#378ADD]/30 bg-[#378ADD]/10 text-[#B5D4F4]" title={t.description}>
                    {t.name}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="noam-overline mb-2">Référentiels pré-chargés</div>
              <div className="flex flex-wrap gap-1.5">
                {result.referentiels_preloaded.map((r, i) => (
                  <span key={`ref-${r}-${i}`} className="text-xs px-2.5 py-1 rounded-full border border-white/10">{r}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="noam-overline mb-2">Widgets dashboard</div>
              <ul className="space-y-1">
                {result.widgets.map((w, i) => <li key={`w-${w}-${i}`} className="text-zinc-300">· {w}</li>)}
              </ul>
            </div>
            <div>
              <div className="noam-overline mb-2">Pour démarrer</div>
              <ul className="space-y-1">
                {result.todos.map((t, i) => <li key={`td-${t}-${i}`} className="text-zinc-300">→ {t}</li>)}
              </ul>
            </div>

            <div className="pt-2 border-t border-white/5">
              <div className="noam-overline mb-2">Document stratégique</div>
              <p className="text-[11px] text-zinc-500 mb-3">
                Importez votre note de cadrage : elle alimentera automatiquement le Rapport de direction.
              </p>
              <DocxUploader module="configurer" compact title="" />
            </div>
          </div>
        )}
      </div>
      </div>
      {result && isAuthenticated && (
        <div className="mt-5 noam-card p-5 flex items-start gap-4 flex-wrap" data-testid="onboarding-finalize">
          <div className="w-11 h-11 rounded-md bg-[#1D9E75]/15 border border-[#1D9E75]/40 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-[#1D9E75]" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="noam-display text-base font-semibold">Activez votre espace en un clic</div>
            <p className="text-zinc-400 text-sm mt-1">
              Sur la base de votre secteur <span className="text-white font-medium">{answers.secteur || "PME"}</span>,
              Noam peut installer un référentiel de 5 risques recommandés que vous pourrez ensuite éditer librement.
            </p>
            {error && <div className="text-[#FCA5A5] text-xs mt-2">{error}</div>}
            {seedDone && <div className="text-[#1D9E75] text-xs mt-2">✓ Référentiel installé. Redirection en cours…</div>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={installStarterPack}
              disabled={seedingPack || seedDone}
              className="btn-primary text-sm disabled:opacity-50"
              data-testid="onboarding-install-pack"
            >
              {seedingPack ? <><Loader2 className="w-4 h-4 animate-spin" /> Installation…</> : <><Plus className="w-4 h-4" /> Installer le référentiel</>}
            </button>
            <button onClick={() => navigate("/app/risques")} className="btn-ghost text-sm" data-testid="onboarding-empty-start">
              Commencer vide
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const PILL_COLORS = [
  "rgba(55,138,221,0.4)",
  "rgba(127,119,221,0.4)",
  "rgba(29,158,117,0.4)",
  "rgba(239,159,39,0.4)",
  "rgba(226,75,74,0.4)",
];
function pillColor(i) { return PILL_COLORS[i % PILL_COLORS.length]; }

const SECTOR_SUGGESTED = {
  "Banque": ["Risques", "Conformité", "Sécurité SI", "DORA"],
  "Assurance": ["Risques", "Conformité", "Continuité", "Solvabilité"],
  "Fonds de pension": ["Risques", "Performance", "Conformité"],
  "Santé": ["Risques cliniques", "Données patients", "Continuité", "ISO 27001", "ISO 45001"],
  "Organisation publique": ["Conformité", "Performance", "RGPD-LPD", "ISO 9001"],
  "Industrie": ["Qualité (ISO 9001)", "Environnement (ISO 14001)", "SST (ISO 45001)", "ESG"],
  "PME": ["Risques", "Conformité", "Performance", "ISO 9001"],
  "Autre": ["Risques", "Conformité"],
};
function sectorSuggestions(s) { return SECTOR_SUGGESTED[s] || ["Risques", "Conformité"]; }

function recommendPhase(maturite) {
  if (maturite?.startsWith("Débutant")) return "Structuration";
  if (maturite?.startsWith("Intermédiaire")) return "Stabilisation";
  return "Déploiement";
}
